import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Home, TrendingUp, User, Mic, MessageSquare } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function InterviewResults() {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [report, setReport] = useState(null);
  const [answers, setAnswers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    setLoading(true);
    try {
      const [interviewRes, reportRes, answersRes] = await Promise.all([
        axios.get(`${API}/interviews/${interviewId}`),
        axios.get(`${API}/interviews/${interviewId}/report`),
        axios.get(`${API}/interviews/${interviewId}/answers`),
      ]);
      setInterview(interviewRes.data);
      setReport(reportRes.data);
      setAnswers(answersRes.data);
    } catch (error) {
      console.error("Error fetching results:", error);
    } finally {
      setLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 80) return "#10b981";
    if (score >= 60) return "#f59e0b";
    return "#ef4444";
  };

  const getScoreLabel = (score) => {
    if (score >= 80) return "Sangat Baik";
    if (score >= 60) return "Baik";
    return "Perlu Perbaikan";
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (!interview || !report) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <p>Hasil tidak ditemukan</p>
        <button className="btn btn-primary" onClick={() => navigate("/")}>
          Ke Beranda
        </button>
      </div>
    );
  }

  return (
    <div className="results-container" data-testid="interview-results">
      <div className="results-header">
        <div style={{ 
          fontSize: "1rem", 
          fontWeight: "700", 
          color: "#0891b2", 
          marginBottom: "1rem",
          letterSpacing: "2px"
        }}>
          RASPRO UNIVERSE
        </div>
        <h1 style={{ fontSize: "3rem", marginBottom: "1rem", color: "#0891b2" }}>
          Hasil Interview
        </h1>
        <div>
          <h2 style={{ fontSize: "1.8rem", marginBottom: "0.5rem" }}>
            {interview.candidate_name}
          </h2>
          <p style={{ color: "#64748b", fontSize: "1.1rem" }}>
            {interview.position} • {report.total_questions} Pertanyaan Dijawab
          </p>
        </div>
        <div
          className="score-display"
          style={{ marginTop: "2rem", color: getScoreColor(report.overall_score) }}
          data-testid="overall-score"
        >
          {report.overall_score}
        </div>
        <div style={{ fontSize: "1.5rem", color: "#64748b", marginTop: "0.5rem" }}>
          {getScoreLabel(report.overall_score)}
        </div>
      </div>

      <div className="scores-grid">
        <div className="score-card" data-testid="facial-score-card">
          <User size={40} color="#0891b2" style={{ marginBottom: "1rem" }} />
          <div className="score-type">Ekspresi Wajah</div>
          <div style={{ fontSize: "2rem", fontWeight: "700", color: "#0891b2", marginTop: "0.5rem" }}>
            {report.facial_avg_score}
          </div>
          <div className="score-bar">
            <div
              className="score-fill"
              style={{ width: `${report.facial_avg_score}%` }}
            />
          </div>
        </div>

        <div className="score-card" data-testid="voice-score-card">
          <Mic size={40} color="#0891b2" style={{ marginBottom: "1rem" }} />
          <div className="score-type">Intonasi Suara</div>
          <div style={{ fontSize: "2rem", fontWeight: "700", color: "#0891b2", marginTop: "0.5rem" }}>
            {report.voice_avg_score}
          </div>
          <div className="score-bar">
            <div
              className="score-fill"
              style={{ width: `${report.voice_avg_score}%` }}
            />
          </div>
        </div>

        <div className="score-card" data-testid="content-score-card">
          <MessageSquare size={40} color="#0891b2" style={{ marginBottom: "1rem" }} />
          <div className="score-type">Kualitas Konten</div>
          <div style={{ fontSize: "2rem", fontWeight: "700", color: "#0891b2", marginTop: "0.5rem" }}>
            {report.content_avg_score}
          </div>
          <div className="score-bar">
            <div
              className="score-fill"
              style={{ width: `${report.content_avg_score}%` }}
            />
          </div>
        </div>
      </div>

      <div
        style={{
          background: "white",
          padding: "2.5rem",
          borderRadius: "20px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          marginBottom: "3rem",
        }}
      >
        <h2 style={{ fontSize: "1.8rem", marginBottom: "1rem", color: "#0891b2" }}>
          <TrendingUp size={30} style={{ verticalAlign: "middle", marginRight: "0.5rem" }} />
          Ringkasan
        </h2>
        <p style={{ color: "#475569", lineHeight: "1.8", fontSize: "1.1rem" }}>
          {report.summary}
        </p>
      </div>

      <div
        style={{
          background: "white",
          padding: "2.5rem",
          borderRadius: "20px",
          boxShadow: "0 4px 20px rgba(0,0,0,0.05)",
          marginBottom: "3rem",
        }}
      >
        <h2 style={{ fontSize: "1.8rem", marginBottom: "2rem", color: "#0891b2" }}>
          Analisis Per Pertanyaan
        </h2>
        {answers.map((answer, index) => (
          <div
            key={answer.id}
            style={{
              padding: "1.5rem",
              background: "#f8fafc",
              borderRadius: "12px",
              marginBottom: "1.5rem",
            }}
            data-testid={`answer-analysis-${index}`}
          >
            <div style={{ fontWeight: "600", marginBottom: "0.5rem", color: "#0891b2", fontSize: "1.1rem" }}>
              Pertanyaan {index + 1}
            </div>
            <div style={{ 
              marginBottom: "1rem", 
              padding: "1rem",
              background: "white",
              borderRadius: "8px",
              color: "#1e293b",
              fontSize: "1rem",
              fontWeight: "500"
            }}>
              {answer.question_text || "Pertanyaan tidak tersedia"}
            </div>
            
            {answer.video_recording && (
              <div style={{ marginBottom: "1.5rem" }}>
                <div style={{ fontWeight: "600", marginBottom: "0.5rem", color: "#0891b2" }}>
                  Rekaman Video Jawaban:
                </div>
                {/* --- KODE KUNCI YANG DIKOREKSI --- */}
                <video
                  controls
                  style={{
                    width: "100%",
                    maxWidth: "600px",
                    borderRadius: "12px",
                    background: "#000",
                  }}
                  // Membangun URL lengkap: BACKEND_URL + path dari DB (/videos/...)
                  src={`${BACKEND_URL}${answer.video_recording}`}
                  type="video/webm" // Tipe MIME yang sesuai dengan rekaman
                  preload="metadata" 
                >
                  Browser Anda tidak mendukung pemutaran video.
                </video>
                {/* --------------------------------- */}
              </div>
            )}
            
            <div style={{ marginBottom: "1rem", color: "#475569" }}>
              <strong>Jawaban:</strong> {answer.transcript}
            </div>
            {answer.analysis && (
              <div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
                    gap: "1rem",
                    marginBottom: "1rem",
                  }}
                >
                  <div>
                    <div style={{ fontSize: "0.85rem", color: "#64748b" }}>Wajah</div>
                    <div style={{ fontSize: "1.3rem", fontWeight: "600", color: "#0891b2" }}>
                      {answer.analysis.facial_score} - {answer.analysis.facial_expression}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: "0.85rem", color: "#64748b" }}>Suara</div>
                    <div style={{ fontSize: "1.3rem", fontWeight: "600", color: "#0891b2" }}>
                      {answer.analysis.voice_score} - {answer.analysis.voice_tone}
                    </div>
                  </div>
                  <div>
                    <div style={{ fontSize: "0.85rem", color: "#64748b" }}>Konten</div>
                    <div style={{ fontSize: "1.3rem", fontWeight: "600", color: "#0891b2" }}>
                      {answer.analysis.content_score} - {answer.analysis.content_quality}
                    </div>
                  </div>
                </div>
                <div style={{ padding: "1rem", background: "white", borderRadius: "8px" }}>
                  <strong style={{ color: "#0891b2" }}>Rekomendasi:</strong>
                  <p style={{ marginTop: "0.5rem", color: "#475569", lineHeight: "1.6" }}>
                    {answer.analysis.recommendations}
                  </p>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: "1rem", justifyContent: "center" }}>
        <button
          className="btn btn-primary"
          onClick={() => navigate("/")}
          data-testid="go-home-btn"
        >
          <Home size={20} />
          Ke Beranda
        </button>
        <button
          className="btn btn-secondary"
          onClick={() => navigate("/admin")}
          data-testid="go-admin-btn"
        >
          Lihat Semua Interview
        </button>
      </div>
    </div>
  );
}
